import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Search, Users, FolderKanban, MoreVertical, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardContent } from '../../components/ui/Card';
import { StaggerList, StaggerItem } from '../../components/animations/StaggerList';
import { api } from '../../lib/api';

interface Client {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  primaryColor: string;
  userCount: number;
  projectCount: number;
  createdAt: string;
}

export function ClientsPage() {
  const [search, setSearch] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchClients() {
      try {
        setIsLoading(true);
        const response = await api.get<Client[]>('/organizations');
        setClients(Array.isArray(response.data) ? response.data : []);
        setError(null);
      } catch (err) {
        setError('Failed to load clients');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchClients();
  }, []);

  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(search.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold">Clients</h1>
          <p className="text-text-secondary mt-1">
            Manage your client organizations
          </p>
        </div>
        <Link to="/clients/new">
          <Button>
            <Plus className="w-4 h-4" />
            New Client
          </Button>
        </Link>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Input
          placeholder="Search clients..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<Search className="w-4 h-4" />}
          className="max-w-md"
        />
      </motion.div>

      {/* Clients Grid */}
      <StaggerList className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredClients.map((client) => (
          <StaggerItem key={client.id}>
            <Link to={`/clients/${client.id}`}>
              <Card className="group cursor-pointer" glow>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                        style={{ backgroundColor: client.primaryColor }}
                      >
                        {client.name.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-semibold group-hover:text-accent transition-colors">
                          {client.name}
                        </h3>
                        <p className="text-sm text-text-muted">{client.slug}</p>
                      </div>
                    </div>
                    <button className="p-1.5 rounded-lg hover:bg-bg-tertiary opacity-0 group-hover:opacity-100 transition-opacity">
                      <MoreVertical className="w-4 h-4 text-text-muted" />
                    </button>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
                    <div className="flex items-center gap-1.5 text-sm text-text-secondary">
                      <Users className="w-4 h-4" />
                      <span>{client.userCount} users</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-text-secondary">
                      <FolderKanban className="w-4 h-4" />
                      <span>{client.projectCount} projects</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </StaggerItem>
        ))}
      </StaggerList>

      {/* Empty state */}
      {filteredClients.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <Users className="w-12 h-12 mx-auto text-text-muted mb-4" />
          <h3 className="text-lg font-semibold mb-2">No clients found</h3>
          <p className="text-text-secondary mb-4">
            {search
              ? 'Try adjusting your search'
              : 'Get started by adding your first client'}
          </p>
          {!search && (
            <Link to="/clients/new">
              <Button>
                <Plus className="w-4 h-4" />
                Add Client
              </Button>
            </Link>
          )}
        </motion.div>
      )}
    </div>
  );
}
